import * as React from 'react';
import autobind from 'autobind-decorator';
import { resolve } from 'inversify-react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';

import { ConfigurationService } from 'src/core/services/configuration.service';
import { ContextService } from 'src/core/services/context.service';
import { HttpService } from 'src/core/services/http.service';
import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { ControllerProps } from 'src/router';
import { EmailDocumentTemplateFormView } from './email-document-template-form.view';
import { EmailDocumentTemplateDataService } from './email-document-template-form.data';
import { IdentityService } from 'src/core/services/identity.service';

interface IEmailDocumentTemplateFormParams {
	id: string,
}

@observer
export class EmailDocumentTemplateFormController extends React.Component<ControllerProps<IEmailDocumentTemplateFormParams>, any> {
	@resolve
	configurationService: ConfigurationService;

	@resolve
	contextService: ContextService;

	@resolve
	httpService: HttpService;

	@resolve
	toast: ToastNotifications;

	@resolve
	identityService: IdentityService;

	@observable
	dataService: EmailDocumentTemplateDataService;

	@observable
	defaultLanguage: string;

	@observable
	isAdmin: boolean;

	@observable
	mode: ControlMode;

	constructor(props: ControllerProps<IEmailDocumentTemplateFormParams>) {
		super(props);

		this.mode = 'display';
	}

	@autobind
	private create() {
		const entity = this.dataService.add(this.defaultLanguage);

		return entity;
	}

	private async loadConfiguration(): Promise<void> {
		const appConfig = await this.configurationService.current();
		this.defaultLanguage = appConfig.defaultLanguage;
	}

	private async loadData(): Promise<void> {
		this.dataService = EmailDocumentTemplateDataService.make(this.contextService);

		this.isAdmin = this.identityService.isAdmin;

		if (this.props.match.params.id == 'new') {
			// Create new
			await this.dataService.create(this.create);
			this.onModeChange('edit');
		} else {
			// Load entity
			this.dataService.addFilter('id', 'eq', this.props.match.params.id);
			this.dataService.addSorting('id', 'asc');
			this.dataService.pageSize = 1;
			await this.dataService.load(query => query);


			this.onModeChange('display');
		}
	}

	@computed
	get title() {
		if (this.dataService && this.dataService.item && this.dataService.item.dynamicProperties.displayName) {
			return `Email Document Template ${this.dataService.item.dynamicProperties['displayName'][this.defaultLanguage]}`;
		}

		return '';
	}

	@autobind
	onModeChange(mode: ControlMode) {
		this.mode = mode;
	}

	@autobind
	async save(): Promise<void> {
		const item = this.dataService.item;
		const errors = await item.validate(true);
		if (errors.length == 0) {

			this.props.setBusy(true);

			try {
				await this.dataService.save();
				this.onModeChange('display');
				this.toast.showDataSaved();
				this.props.history.replace(`/general/email-document-templates`);
			} catch (error) {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			} finally {
				this.props.setBusy(false);
			}
		} else {
			this.toast.showToast('Validation error!', errors.map(e => e.text), 'error');
		}
	}

	@autobind
	cancel(): void {
		this.props.history.push('/general/email-document-templates');
	}

	componentDidMount() {
		this.props.setBusy(true);

		Promise.all([this.loadConfiguration(), this.loadData()])
			.catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			})
			.then(() => {
				this.props.setBusy(false);
			});
	}

	render() {
		if (this.dataService == null || this.dataService.item == null) {
			return null;
		}

		return (
			<EmailDocumentTemplateFormView
				title={this.title}
				model={this.dataService.item}
				save={this.save}
				cancel={this.cancel}
				mode={this.mode}
				onModeChange={this.onModeChange}
				setBusy={this.props.setBusy}
				contextService={this.contextService}
				isAdmin={this.isAdmin}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/general/email-document-template/form/email-document-template-form.controller.tsx